import API from "helpers/api";
import { Apis } from "services/api";
import { getAPIResponse } from "helpers/getApiResponse";
import { defaultPageSize } from "helpers/utils";
import { processChipsForAPI } from "helpers/chipProcessor";

// Utility function to transform parameters to new ModelSearchPostRequest format
const transformToModelSearchRequest = ({
	search = "",
	siteDepartmentId = "",
	siteDepartmentIds = [],
	modelStatusId = "",
	chips = [],
	...otherParams
}) => {
	// Process chips using centralized utility function to get individual arrays
	const chipsByType = processChipsForAPI(chips);

	// Add single department ID to siteDepartmentIds array if present
	if (siteDepartmentId && siteDepartmentId !== "") {
		const deptId = parseInt(siteDepartmentId);
		if (!isNaN(deptId) && deptId !== 0 && !chipsByType.siteDepartmentIds.includes(deptId)) {
			chipsByType.siteDepartmentIds.push(deptId);
		}
	}

	// Add multiselect department IDs array if present
	if (siteDepartmentIds && Array.isArray(siteDepartmentIds) && siteDepartmentIds.length > 0) {
		siteDepartmentIds.forEach(id => {
			const deptId = parseInt(id);
			if (!isNaN(deptId) && deptId !== 0 && !chipsByType.siteDepartmentIds.includes(deptId)) {
				chipsByType.siteDepartmentIds.push(deptId);
			}
		});
	}

	// Add model status to a modelStatusIds array if present
	const modelStatusIds = [];
	if (modelStatusId && modelStatusId !== "") {
		modelStatusIds.push(modelStatusId);
	}

	// Add search text to textSearches array if present
	if (search && search !== "") {
		if (!chipsByType.textSearches.includes(search)) {
			chipsByType.textSearches.push(search);
		}
	}

	// Get timezone
	const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

	// Return transformed request WITHOUT chips property
	// Instead, spread the individual arrays from chipsByType
	return {
		...otherParams,
		timeZone,
		modelStatusIds,
		// Individual arrays instead of chips
		modelTypeIds: chipsByType.modelTypeIds,
		userIds: chipsByType.userIds,
		siteAssetIds: chipsByType.siteAssetIds,
		modelIds: chipsByType.modelIds,
		textSearches: chipsByType.textSearches,
		siteDepartmentIds: chipsByType.siteDepartmentIds,
	};
};

const getAvailableModelDepartments = async (id) => {
	try {
		let response = await API.get(`${Apis.SiteDepartments}?siteAppId=${id}`);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};
const getModelImportsClientList = async (id) => {
	try {
		let response = await API.get(`${Apis.ModelImports}/clients`);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};
const getClientDetail = async (id) => {
	try {
		let response = await API.get(`${Apis.Clients}/${id}`);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};
const getDeletedModels = async () => {
	try {
		let response = await API.get(`${Apis.Models}/deleted`);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};
const restoreDeletedModels = async (id) => {
	try {
		let response = await API.post(`${Apis.Models}/${id}/restore`);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};
const getModelImportsSites = async (id) => {
	try {
		let response = await API.get(`${Apis.ModelImports}/sites?clientId=${id}`);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};
const getModelImportsApplications = async (id, modelType) => {
	try {
		let response = await API.get(
			`${Apis.ModelImports}/applications?siteId=${id}&modelType=${modelType}`
		);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};
const transferModel = async (payload) => {
	try {
		let response = await API.post(`${Apis.ModelImports}/transfer`, payload);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

const getModelDepartments = async (id) => {
	try {
		let response = await API.get(`${Apis.SiteDepartments}?siteAppId=${id}`);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

const getModelStatuses = async (id) => {
	try {
		let response = await API.get(`${Apis.ModelStatuses}?siteAppId=${id}`);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

const getPublishedModel = async (id) => {
	try {
		let response = await API.get(`${Apis.Models}/published?siteAppId=${id}`);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

const addModal = async (payload) => {
	try {
		let response = await API.post(`${Apis.Models}`, payload);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

const duplicateModal = async (payload) => {
	try {
		let response = await API.post(`${Apis.DuplicateModal}`, payload);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

const duplicateAndTranslateModel = async (payload) => {
	try {
		let response = await API.post(`${Apis.DuplicateAndTranslateModal}`, payload);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

const getListOfModelVersions = async (id) => {
	try {
		let response = await API.get(`${Apis.Models}/${id}/${Apis.Versions}`);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

const getModelImports = async (id) => {
	try {
		let response = await API.get(`${Apis.ModelImports}?siteAppId=${id}`);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};
const getModelAutocompleteSearch = async ({ search = "" }) => {
	try {
		// The query-string route is not deployed to production yet, so the
		// path-segment form is the only one the live API currently answers.
		let response = await API.get(`${Apis.ModelsAutocomplete}/${search}`);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

const getModelList = async ({
	id,
	pageNumber = "1",
	pageSize = defaultPageSize(),
	search = "",
	sortField = "",
	sortOrder = "",
	fromDate = "",
	toDate = "",
	siteDepartmentId = "",
	siteDepartmentIds = [],
	chips = [],
	modelStatusId,
}) => {
	try {
		// Transform parameters to new request format
		const payload = transformToModelSearchRequest({
			id,
			pageNumber,
			pageSize,
			search,
			sortField,
			sortOrder,
			fromDate,
			toDate,
			siteDepartmentId,
			siteDepartmentIds,
			chips,
			modelStatusId,
		});

		let response = await API.post(`${Apis.ModelsSearch}`, payload);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

const getCountOfModelList = async ({
	id,
	search = "",
	sortField = "",
	sortOrder = "",
	fromDate = "",
	toDate = "",
	siteDepartmentId = "",
	siteDepartmentIds = [],
	chips = [],
	modelStatusId,
}) => {
	try {
		// Transform parameters to new request format (same as search)
		const payload = transformToModelSearchRequest({
			id,
			search,
			sortField,
			sortOrder,
			fromDate,
			toDate,
			siteDepartmentId,
			siteDepartmentIds,
			chips,
			modelStatusId,
		});

		let response = await API.post(`${Apis.Models}/count`, payload);
		return getAPIResponse(response);
	} catch (err) {
		return getAPIResponse(err?.response);
	}
};

export {
	getModelList,
	getCountOfModelList,
	getModelStatuses,
	addModal,
	getAvailableModelDepartments,
	duplicateModal,
	duplicateAndTranslateModel,
	getListOfModelVersions,
	getModelImports,
	getPublishedModel,
	getModelDepartments,
	getModelImportsClientList,
	getModelImportsSites,
	getModelImportsApplications,
	transferModel,
	getClientDetail,
	getDeletedModels,
	restoreDeletedModels,
	getModelAutocompleteSearch,
};
